import {
  Avatar,
  ChoiceList,
  TextStyle,
  Button,
  ButtonGroup,
  Modal,
  Page,
  Heading,
  DisplayText,
  Checkbox,
  Badge,
  TextField
} from '@superops/ui-design-system';
import React, { useCallback, useReducer, useState, useMemo } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { debounce, isEmpty } from 'lodash';
import {
  Column,
  DateTimeFormatter,
  getTranslatedText,
  Loader,
  Row,
  useUserMSPCheckHook,
  Pagination,
  toast,
  EventEmitter,
  NoDataCard,
  useProduct,
  COMMON_CONSTANTS,
  TICKET_SOURCE
} from '@nile/components/src';
import { GET_TICKET_LIST, MERGE_TICKET } from '@nile/graphql/src';
import { useMutation, useQuery } from '@apollo/client';
import envConfig from '@nile/home/src/Config/env';
import { Tooltip } from 'react-clone-tippy';

function TicketItem({
  record,
  leftIcon,
  isParentTicket,
  onSetAsParentClick,
  hideHover
}) {
  if (!record) return null;

  const { subject, requester, site, createdTime } = record;

  const displayName =
    requester?.name ||
    `${requester?.firstName || ''} ${requester?.lastName || ''}` ||
    '';

  return (
    <div
      className={`p10 rounded-4px visibilityContainer full-width ${!hideHover &&
        'purple-hover'}`}
    >
      <Row gap={10} className="full-width">
        {leftIcon}
        <Column gap={15} className="full-width">
          <Row gap={10}>
            <DisplayText element="p" size="large" weight="SemiBold">
              <span className="word-break-all">{subject}</span>
            </DisplayText>
            {isParentTicket && (
              <Badge status="success">
                <FormattedMessage id="ticket.details.merge.parentTicket" />
              </Badge>
            )}
            {onSetAsParentClick && (
              <div className="mlauto visibilityActions">
                <Button
                  onClick={onSetAsParentClick}
                  icon={<SVGIcon name="TicketOutline" />}
                >
                  <DisplayText element="p" size="large" weight="Medium">
                    <FormattedMessage id="ticket.details.merge.setAsParentTicket" />
                  </DisplayText>
                </Button>
              </div>
            )}
          </Row>
          <Row gap={30} className="full-width">
            <DisplayText element="p" size="medium" weight="Regular">
              <span className="nowrap">
                <FormattedMessage
                  id="ticket.details.merge.listItem.displayId"
                  values={{ id: (hash) => `${hash}${record.displayId}` }}
                />
              </span>
            </DisplayText>
            {!isEmpty(displayName.trim()) ? (
              <div className="fx ellipsis hfc fb-30">
                <Avatar
                  initials={displayName.slice(0, 1).toUpperCase()}
                  name={displayName}
                  size="extraSmall"
                />
                <span title={displayName} className="fw ellipsis ml8">
                  <TextStyle>{displayName}</TextStyle>
                </span>
              </div>
            ) : (
              <span className="fx hfc fb-30">
                <TextStyle variation="subdued">NA</TextStyle>
              </span>
            )}
            {site?.name && (
              <Row gap={5} className="fb-30">
                <SVGIcon name="Location" />
                <DisplayText element="h4">{site?.name}</DisplayText>
              </Row>
            )}
            <Row gap={5} className="fb-30">
              <SVGIcon name="ClockRound" />
              <DisplayText element="h4">
                <DateTimeFormatter type="dateTime" date={createdTime} />
              </DisplayText>
            </Row>
          </Row>
        </Column>
      </Row>
    </div>
  );
}

function MergeContainer(props) {
  const {
    records,
    selectTicket,
    selected,
    merge,
    toggleSheetActive,
    loading,
    children
  } = props;

  return (
    <Page
      title={<FormattedMessage id="ticket.details.mergeTickets.modal.header" />}
      variant="primary"
      contentHeight="100px"
      primaryAction={
        <ButtonGroup className="fx mlauto">
          <Button
            id="mergeTicketPage"
            onClick={toggleSheetActive}
            plainSecondary
          >
            <FormattedMessage id="common.cancel" />
          </Button>
          <Button
            onClick={() => {
              merge(toggleSheetActive);
            }}
            primary
            icon={<SVGIcon name="Merge" />}
            loading={loading}
          >
            <FormattedMessage id="ticket.details.merge.merge" />
          </Button>
        </ButtonGroup>
      }
    >
      {children ?? (
        <div className="flex col g30">
          <div>
            <DisplayText element="p" size="extraLarge" weight="SemiBold">
              <FormattedMessage id="ticket.details.merge.header" />
            </DisplayText>
            <TextStyle variation="subdued">
              <FormattedMessage id="ticket.details.merge.modal.header.helpSubText" />
            </TextStyle>
          </div>

          <div className="fh scroll min-height-300">
            <ChoiceList
              choices={records
                .filter(
                  (record) => record.source !== TICKET_SOURCE.Communication //Instant messaging source shouldnt be merged so filter it out and display other sources
                )
                .map((record) => ({
                  label: <TicketItem record={record} hideHover />,
                  value: record.ticketId
                }))}
              selected={selected}
              onChange={selectTicket}
              inputMargin="10px 0 0 0"
              fullWidth
            />
          </div>
        </div>
      )}
    </Page>
  );
}

function MergeTicketButtonWithModal({ merge, ...props }) {
  const [sheetActive, setSheetActive] = useState(false);

  const toggleSheetActive = useCallback(() => setSheetActive((s) => !s), []);

  const [checked, setChecked] = useReducer((s, ns) => ({ ...s, ...ns }), {});

  //to filter the source except instant messaging as instant messaging source shouldnt be merged
  const filteredRecords = props?.records?.filter(
    (record) => record.source !== TICKET_SOURCE.Communication
  );
  return (
    <div>
      {/* Instant messaging shouldnt be merged as hide merge button in toolbar */}
      {filteredRecords?.length > 0 && (
        <Button onClick={toggleSheetActive} icon={<SVGIcon name="Merge" />}>
          <FormattedMessage id="ticket.details.merge.merge" />
        </Button>
      )}

      <MergeTicketModal
        isOpen={sheetActive}
        toggleModal={toggleSheetActive}
        checked={checked}
        onCheckedChange={setChecked}
        merge={(closeModalCb) =>
          merge(closeModalCb, {
            moveRecipients: checked.addRecipientsToCC,
            moveWorklog: checked.addTimerAndWorklog
          })
        }
        {...props}
      />
    </div>
  );
}

export function MergeTicketsFromDetails({
  toggleModal,
  activeTicket,
  refetchTicket
}) {
  const [checked, setChecked] = useReducer((s, ns) => ({ ...s, ...ns }), {});

  const [searchText, setSearchText] = useState('');

  const [parentTicket, setParentTicket] = useState(activeTicket);

  // const [yetToBeMergedTickets, setYetToBeMergedTickets] = useState([]);
  const [stagedTickets, setStagedTickets] = useState([]);

  const intl = useIntl();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const [loadingTicketListRefetch, setLoadingTicketListRefetch] = useState(
    false
  );

  const [mergeTickets, { loading: mergeTicketLoading }] = useMutation(
    MERGE_TICKET,
    {
      onCompleted(data) {
        if (data?.combineTickets) {
          toast('success', getTranslatedText(intl, 'ticket.merge.toast'));
          refetchTicket();
          toggleModal();
          EventEmitter.dispatch('refetchTicketAudit');
        }
      }
    }
  );

  function onMerge() {
    mergeTickets({
      variables: {
        input: {
          parentId: parentTicket.ticketId,
          childIds: stagedTickets.map((ticket) => ticket.ticketId),
          moveRecipients: checked.addRecipientsToCC,
          moveWorklog: checked.addTimerAndWorklog
        }
      }
    });
  }

  function handleStaging(record) {
    setStagedTickets((curr) => [...curr, record]);
  }

  function handleUnStaging(record) {
    setStagedTickets((curr) =>
      curr.filter((currRecord) => currRecord.ticketId !== record.ticketId)
    );
  }

  const isMSP = useUserMSPCheckHook();

  function getConditions() {
    if (isMSPProduct) {
      return {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'client.accountId',
            operator: 'is',
            value: parentTicket?.client?.accountId
          }
        ]
      };
    }
    if (parentTicket?.site?.id) {
      return {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'site.id',
            operator: 'is',
            value: parentTicket?.site?.id
          }
        ]
      };
    }
    return null;
  }

  const ticketFetchListInfo = useMemo(
    () => ({
      pageSize: 20,
      page: 1,
      sort: [
        {
          attribute: 'createdTime',
          order: 'DESC'
        }
      ],
      condition: getConditions(),
      view: { id: isMSP ? '1' : '15' }
    }),
    [isMSP, parentTicket]
  );

  const getListInfoCondition = useCallback(
    ({ searchTerm, parentTicket, stagedTickets }) => {
      return {
        ...ticketFetchListInfo.condition,
        operands: [
          ...ticketFetchListInfo.condition.operands,
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'ticketId',
                operator: 'notIncludes',
                value: [
                  ...stagedTickets.map((ticket) => ticket.ticketId),
                  parentTicket.ticketId,
                  parentTicket.parentTicket?.ticketId,
                  ...(parentTicket.childTickets ?? []).map(
                    (ticket) => ticket.ticketId
                  )
                ].filter(Boolean)
              }
            ]
          },
          searchTerm && {
            joinOperator: 'AND',
            operands: [
              {
                joinOperator: 'OR',
                operands: [
                  {
                    attribute: 'subject',
                    operator: 'contains',
                    value: searchTerm
                  },
                  {
                    attribute: 'displayId',
                    operator: 'contains',
                    value: searchTerm
                  }
                ]
              }
            ]
          },
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'status',
                operatorValue: 'placeholder.ticket.status.closedandresolved'
              }
            ]
          },
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'source',
                operator: 'notIncludes',
                value: TICKET_SOURCE.Communication
              }
            ]
          }
        ].filter(Boolean)
      };
    },
    [ticketFetchListInfo]
  );

  const {
    data: ticketListData,
    loading: loadingTicketList,
    refetch: refetchTicketList
    // networkStatus: ticketListNetworkStatus
  } = useQuery(GET_TICKET_LIST, {
    fetchPolicy: 'network-only',
    variables: {
      listInfo: {
        ...ticketFetchListInfo,
        condition: getListInfoCondition({
          searchTerm: '',
          parentTicket: activeTicket,
          stagedTickets: []
        })
      },
      isMSPProduct
    }
  });

  const { ticketList, listInfo } = useMemo(() => {
    if (!ticketListData?.getTicketList) return { ticketList: [], listInfo: {} };

    return {
      ticketList: ticketListData.getTicketList.tickets,
      listInfo: ticketListData.getTicketList.listInfo
    };
  }, [ticketListData]);

  const filteredTicketList = useMemo(() => {
    return ticketList?.filter((r) => {
      const checkChildTickets = !parentTicket.childTickets?.some(
        (childTicket) => childTicket.ticketId === r.ticketId
      );

      const checkParentTicket = parentTicket.parentTicket
        ? parentTicket.parentTicket.ticketId !== r.ticketId
        : true;

      return (
        !stagedTickets.some((s) => s.ticketId === r.ticketId) &&
        r.ticketId !== parentTicket.ticketId &&
        checkChildTickets &&
        checkParentTicket
      );
    });
  }, [ticketList, stagedTickets, parentTicket]);

  const handleTicketRefetch = useCallback(
    ({ pageNumber, searchTerm }) => {
      const condition = getListInfoCondition({
        searchTerm,
        parentTicket,
        stagedTickets
      });

      setLoadingTicketListRefetch(true);

      refetchTicketList({
        listInfo: {
          ...ticketFetchListInfo,
          condition,
          page: pageNumber
        }
      }).finally(() => {
        setLoadingTicketListRefetch(false);
      });
    },
    [
      ticketFetchListInfo,
      refetchTicketList,
      getListInfoCondition,
      parentTicket,
      stagedTickets
    ]
  );

  const debouncedFetchList = useMemo(
    () =>
      debounce((value) => {
        handleTicketRefetch({ searchTerm: value });
      }, 300),
    [handleTicketRefetch]
  );

  function onSearch(value) {
    setSearchText(value);
    debouncedFetchList(value);
  }

  function handleSetAsParentClick(ticket) {
    setParentTicket(ticket);

    const newStagedTickets = [parentTicket];

    stagedTickets.forEach((record) => {
      if (record.ticketId !== ticket.ticketId) {
        newStagedTickets.push(record);
      }
    });

    setStagedTickets(newStagedTickets);
  }

  return (
    <MergeTicketModal
      isOpen
      toggleModal={toggleModal}
      checked={checked}
      onCheckedChange={setChecked}
      merge={onMerge}
      loading={mergeTicketLoading}
    >
      {/* Staging Area */}
      <section className="mb-30 w-80">
        <TicketItem record={parentTicket} isParentTicket hideHover />

        {stagedTickets?.map((record) => (
          <TicketItem
            key={record.ticketId}
            record={record}
            leftIcon={
              <div
                className="cursor-pointer hfc"
                onClick={() => handleUnStaging(record)}
              >
                <SVGIcon name="CircleMinus" color="#C14136" />
              </div>
            }
            onSetAsParentClick={() => handleSetAsParentClick(record)}
          />
        ))}
      </section>

      {/* Search Bar */}
      <Column as="section" gap={15}>
        <Row>
          <Column gap={5}>
            <DisplayText element="h2" size="extraLarge" weight="SemiBold">
              <FormattedMessage id="ticket.details.merge.chooseTicketsToMerge" />
            </DisplayText>
            <TextStyle>
              <FormattedMessage id="ticket.details.merge.selectTickets.subText" />
            </TextStyle>
          </Column>
          {/* Pagination */}
          {loadingTicketList ||
          Math.ceil(listInfo.totalCount / listInfo.pageSize) <= 1 ? null : (
            <Pagination
              onPageChange={(pageNumber) => handleTicketRefetch({ pageNumber })}
              rowCount={20}
              recordCount={listInfo?.totalCount}
              pages={
                listInfo
                  ? Math.ceil(listInfo.totalCount / listInfo.pageSize)
                  : 0
              }
              page={listInfo?.page || 0}
              setRowCount={() => {}}
              dynamicRowCount={false}
              isClientPagination={false}
              renderPagination={<CustomPagination />}
            />
          )}
        </Row>
        <TextField
          onChange={onSearch}
          value={searchText}
          placeholder={getTranslatedText(intl, 'app.filter.search.label')}
          variant="outline"
          prefix={<SVGIcon name="Search" />}
          clearButton
          onClearButtonClick={() => onSearch('')}
        />
        {/* Open and In progress tickets */}
        <section className="w-80">
          {loadingTicketList ? (
            <Loader />
          ) : (
            <Column
              as="section"
              gap={20}
              className={`items-center ${loadingTicketListRefetch &&
                'opacity-50 pointer-events-none'}`}
            >
              {(() => {
                if (filteredTicketList.length === 0) {
                  return (
                    <NoDataCard
                      imageURL={`url(${`${envConfig.imageRoot}svg/not-found.svg`})`}
                      text={
                        <FormattedMessage id="ticket.details.merge.noData" />
                      }
                      type="secondary"
                    />
                  );
                }

                return filteredTicketList.map((record) => (
                  <TicketItem
                    key={record.ticketId}
                    record={record}
                    leftIcon={
                      <Tooltip
                        distance={35}
                        title={getTranslatedText(
                          intl,
                          'ticket.details.merge.addToMerge'
                        )}
                        position="top"
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                      >
                        <div
                          className="cursor-pointer hfc"
                          onClick={() => handleStaging(record)}
                        >
                          <SVGIcon name="CirclePlus" color="#8CBE62" />
                        </div>
                      </Tooltip>
                    }
                  />
                ));
              })()}
            </Column>
          )}
        </section>
      </Column>
    </MergeTicketModal>
  );
}

function MergeTicketModal({
  isOpen,
  toggleModal,
  checked,
  onCheckedChange,
  ...rest
}) {
  return (
    <Modal
      open={isOpen}
      onClose={toggleModal}
      title=""
      showCloseButton={false}
      large
      noScroll
    >
      <div className="ch-115">
        <MergeContainer toggleSheetActive={toggleModal} {...rest} />
        {/* Footer */}
        <Column gap={15} className="pt20 pl-40 pb20 white-bg bshadow">
          <Checkbox
            label={
              <Row gap={10}>
                <SVGIcon name="Email" />
                <DisplayText element="p" size="large" weight="Medium">
                  <FormattedMessage id="ticket.details.addRecipients" />
                </DisplayText>
              </Row>
            }
            checked={checked.addRecipientsToCC}
            onChange={(check) => onCheckedChange({ addRecipientsToCC: check })}
          />
          <Checkbox
            label={
              <Row gap={10}>
                <SVGIcon name="TimerNav" />
                <DisplayText element="p" size="large" weight="Medium">
                  <FormattedMessage id="ticket.details.merge.addWorklog" />
                </DisplayText>
              </Row>
            }
            checked={checked.addTimerAndWorklog}
            onChange={(check) => onCheckedChange({ addTimerAndWorklog: check })}
          />
        </Column>
      </div>
    </Modal>
  );
}

function CustomPagination({
  previousPage,
  isPreviousDisabled,
  nextPage,
  isNextDisabled,
  totalCount,
  activePage,
  totalPages,
  startCount,
  endCount
}) {
  return (
    <Row className="center jctr mlauto" gap={10}>
      <DisplayText element="p" size="medium" weight="Regular">
        {`Showing ${startCount} - ${endCount} of ${totalCount}`}
      </DisplayText>

      <Button
        disabled={isPreviousDisabled}
        onClick={previousPage}
        icon={<SVGIcon name="ArrowLeft" />}
      />

      <Button
        disabled={isNextDisabled}
        onClick={nextPage}
        icon={<SVGIcon name="ArrowRight" />}
      />
    </Row>
  );
}

const MergeTicketSheet = injectIntl(MergeTicketButtonWithModal);

export default MergeTicketSheet;
